import React, { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import {
  DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../../components/ui/select';
import { useToast } from '../../../../hooks/use-toast';
import { createAdminUser } from '../../../../services/AdminApis'; // Adjust import path

// This should match the AdminGroup enum in your Python code
const ADMIN_GROUPS = [
  "SuperAdmin", "SalesAdmin", "MarketingAdmin", "SEOUser", "CategoryManager",
  "CXAdmin", "CXAgent", "CXTL", "SalesAgent", "SalesTL", "AffiliateManager", "AffiliateAgent", "SalesContractor",
  "EnterpriseSalesManager"
];

export const CreateUserDialog = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [group, setGroup] = useState('');
  const [zohoUserId, setZohoUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !group) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill out both email and group.",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createAdminUser({
        email,
        group,
        first_name: firstName || null,
        last_name: lastName || null,
        zoho_user_id: zohoUserId || null,
      });
      toast({
        variant: "success",
        title: "User Created",
        description: `${email} has been pre-provisioned. They can now log in.`,
      });
      onSuccess(); // This will close the dialog and refresh the list
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Creation Failed",
        description: error.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>Create Admin User</DialogTitle>
          <DialogDescription>
            Pre-provision a new admin user. They will be fully activated on their first login.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="col-span-3"
              placeholder="new.admin@example.com"
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="first_name" className="text-right">First Name</Label>
            <Input
              id="first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="last_name" className="text-right">Last Name</Label>
            <Input
              id="last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="zoho_user_id" className="text-right">Zoho CRM User ID</Label>
            <Input
              id="zoho_user_id"
              value={zohoUserId}
              onChange={(e) => setZohoUserId(e.target.value)}
              className="col-span-3"
              placeholder="e.g. 986323000000415001"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="group" className="text-right">Group</Label>
            <Select onValueChange={setGroup} value={group} required>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_GROUPS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">Cancel</Button>
          </DialogClose>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};